// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ExpenseType, Feedback } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogDeleteExpenseTypeProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogDeleteExpenseType = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogDeleteExpenseTypeProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramExpenseType = searchParams.get('dialogExpenseType');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [expenseType, setExpenseType] = useState<ExpenseType | null>(null);

	// fetch expense type
	const { isLoading: isLoadingExpenseType } = useFetch({
		isEnabled: Boolean(paramExpenseType && paramDialog === 'deleteExpenseType'),
		url: `${process.env.REACT_APP_API_URL}/expense-types/${paramExpenseType}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setExpenseType(res.data[0]);
			}
		},
	});

	// delete expense type
	const { fetchRequest: deleteExpenseType, isLoading: isLoadingDeleteExpenseType } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/expense-types/${paramExpenseType}`,
		options: {
			method: 'DELETE',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onError: (error) => {
			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (
				['EXPENSE_TYPE_DOCUMENT_TYPES_EXIST', 'EXPENSE_TYPE_TRANSACTIONS_EXIST'].includes(error.id)
			) {
				feedbackNew.title = {
					children: t('expenseTypeCannotBeDeleted'),
				};

				if (error.id === 'EXPENSE_TYPE_DOCUMENT_TYPES_EXIST') {
					feedbackNew.alert.children = t('error.expenseTypeDocumentTypesExist');
				}

				if (error.id === 'EXPENSE_TYPE_TRANSACTIONS_EXIST') {
					feedbackNew.alert.children = t('error.expenseTypeTransactionsExist');
				}
			}

			if (error.id === 'EXPENSE_TYPE_UNAUTHORIZED_DELETE') {
				feedbackNew.alert.children = t('error.unauthorized');
			}

			setFeedback(feedbackNew);
		},
		onSuccess: () => {
			// set banner
			setBanner({
				hasClose: true,
				message: t('alert.expenseTypeDeleted', { name: expenseType?.name }),
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// delete expense type
		deleteExpenseType();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingExpenseType) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey="dialogDeleteExpenseType.content"
						values={{ name: expenseType?.name }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{t('alert.deleteExpenseType')}</Alert>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('deleteExpenseType'),
					color: 'error',
					loading: isLoadingDeleteExpenseType,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'expenseType',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			subtitle={{
				children: isLoadingExpenseType ? '' : expenseType?.name,
			}}
			title={{
				children: t('deleteExpenseType'),
			}}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
